import React from 'react';
import { Link } from 'react-router-dom';

const appLogo = new URL('../assets/bbyb-logo.png', import.meta.url);

const Section = ({ title, children }) => (
    <section className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 sm:p-8">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">{title}</h2>
        <div className="text-gray-600 text-sm leading-relaxed space-y-3">{children}</div>
    </section>
);

const TermsPage = () => {
    const effectiveDate = 'May 4, 2026';

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#f9f4f0' }}>
            <header className="text-white shadow-lg" style={{ background: 'linear-gradient(to right, #660F09, #895129)' }}>
                <div className="max-w-4xl mx-auto px-4 py-4 sm:px-6 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2 hover:opacity-90 transition">
                        <img src={appLogo.href} alt="BrickByBrickVault" className="h-7 w-auto rounded" />
                        <span className="text-lg font-bold tracking-tight">BrickByBrickVault</span>
                    </Link>
                    <Link
                        to="/"
                        className="text-sm font-medium px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition"
                    >
                        Back to app
                    </Link>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 py-10 sm:px-6 sm:py-14">
                <div className="text-center mb-12">
                    <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 mb-3">Terms of Service</h1>
                    <p className="text-gray-500 text-sm">Effective date: {effectiveDate}</p>
                </div>

                <div className="space-y-6">
                    <Section title="1. About These Terms">
                        <p>
                            These Terms of Service govern access to and use of BrickByBrickVault, a private file storage
                            and backup service. By using the service, you agree to follow these terms and any account
                            policies provided by the administrator.
                        </p>
                        <p>
                            This is a boilerplate terms page for an invitation-only service and should be reviewed by
                            qualified counsel before use in a commercial or public launch.
                        </p>
                    </Section>

                    <Section title="2. Eligibility and Account Access">
                        <p>
                            Access is limited to users who have been invited or approved by the service administrator.
                            You are responsible for keeping your credentials secure and for any activity performed through
                            your account.
                        </p>
                        <p>
                            You must notify the administrator promptly if you believe your account has been compromised.
                        </p>
                    </Section>

                    <Section title="3. Acceptable Use">
                        <p>You agree not to use BrickByBrickVault to store, upload, share, or process content that:</p>
                        <ul className="list-disc list-inside space-y-1 mt-2">
                            <li>Violates applicable law or another person's rights.</li>
                            <li>Contains malware, exploit code, or material intended to disrupt systems.</li>
                            <li>Attempts to bypass authentication, quotas, storage rules, or security controls.</li>
                            <li>Creates excessive load or interferes with the service for other users.</li>
                        </ul>
                    </Section>

                    <Section title="4. Your Files and Content">
                        <p>
                            You retain ownership of the files and content you upload. You grant BrickByBrickVault the
                            limited permission needed to store, back up, retrieve, display, and process that content for
                            the purpose of providing the service.
                        </p>
                        <p>
                            You are responsible for ensuring you have the rights needed to upload and store your content.
                        </p>
                    </Section>

                    <Section title="5. Storage, Availability, and Backups">
                        <p>
                            BrickByBrickVault is designed for secure storage and sync, but no service can guarantee
                            uninterrupted availability or absolute protection against data loss. You should keep independent
                            backups of critical files.
                        </p>
                        <p>
                            Storage quotas, retention settings, cold storage behavior, and account limits may be adjusted
                            by the administrator.
                        </p>
                    </Section>

                    <Section title="6. Privacy and Security">
                        <p>
                            Use of the service is also governed by the{' '}
                            <Link to="/privacy" className="font-medium underline" style={{ color: '#895129' }}>
                                Privacy Policy
                            </Link>
                            . The service uses reasonable technical safeguards, including encrypted transport and protected
                            authentication flows, to help secure account and file data.
                        </p>
                    </Section>

                    <Section title="7. Account Suspension and Termination">
                        <p>
                            The administrator may suspend or terminate access if an account violates these terms, creates
                            security risk, exceeds permitted usage, or is no longer authorized to use the service.
                        </p>
                        <p>
                            You may request account deletion through the service settings or by contacting the administrator.
                        </p>
                    </Section>

                    <Section title="8. Disclaimers">
                        <p>
                            The service is provided on an "as is" and "as available" basis. To the extent permitted by law,
                            BrickByBrickVault disclaims warranties of uninterrupted operation, error-free functionality, and
                            fitness for a particular purpose.
                        </p>
                    </Section>

                    <Section title="9. Limitation of Liability">
                        <p>
                            To the extent permitted by law, BrickByBrickVault and its operators will not be liable for
                            indirect, incidental, consequential, special, or punitive damages, or for loss of data, profits,
                            or business opportunities arising from use of the service.
                        </p>
                    </Section>

                    <Section title="10. Changes to These Terms">
                        <p>
                            These terms may be updated from time to time. The effective date at the top of this page reflects
                            the most recent revision. Continued use of the service after changes are posted means you accept
                            the updated terms.
                        </p>
                    </Section>

                    <Section title="11. Contact">
                        <p>
                            For questions about these terms, contact the administrator at the email address associated with
                            your invitation or account.
                        </p>
                    </Section>
                </div>

                <p className="text-center text-gray-400 text-xs mt-12">
                    BrickByBrickVault &middot; Terms of Service &middot; {effectiveDate}
                </p>
            </main>
        </div>
    );
};

export default TermsPage;
